import { useState, useEffect } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { supabase } from '../../../lib/supabase';

interface WithdrawModalProps {
  onClose: () => void;
  onSuccess: () => void;
}

export default function WithdrawModal({ onClose, onSuccess }: WithdrawModalProps) {
  const { user, profile } = useAuth();
  const [amount, setAmount] = useState('');
  const [bankName, setBankName] = useState('');
  const [accountNumber, setAccountNumber] = useState('');
  const [accountHolder, setAccountHolder] = useState('');
  const [loading, setLoading] = useState(false);
  const [balance, setBalance] = useState(0);

  const MIN_WITHDRAW = 10000; // 최소 환급 금액 (10,000 달란트)
  const EXCHANGE_RATE = 1; // 1 달란트 = 1원

  useEffect(() => {
    loadBalance();
    if (profile?.full_name) {
      setAccountHolder(profile.full_name);
    }
  }, [profile]);

  const loadBalance = async () => {
    if (!user) return;

    try {
      const { data, error } = await supabase
        .from('talent_points')
        .select('balance')
        .eq('user_id', user.id)
        .single();

      if (error) throw error;
      setBalance(data?.balance || 0);
    } catch (error) {
      console.error('잔액 조회 실패:', error);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!user || !amount || !bankName || !accountNumber || !accountHolder) {
      alert('모든 필드를 입력해주세요.');
      return;
    }

    const withdrawAmount = parseInt(amount);

    if (withdrawAmount < MIN_WITHDRAW) {
      alert(`최소 ${MIN_WITHDRAW.toLocaleString()} 달란트부터 환급 신청이 가능합니다.`);
      return;
    }

    if (withdrawAmount > balance) {
      alert('보유 달란트가 부족합니다.');
      return;
    }

    setLoading(true);

    try {
      // 환급 신청 생성
      await supabase
        .from('talent_withdrawals')
        .insert({
          user_id: user.id,
          amount: withdrawAmount,
          bank_name: bankName,
          account_number: accountNumber,
          account_holder: accountHolder,
          status: 'pending',
        });

      // 달란트 차감
      const { data: pointData, error: pointError } = await supabase
        .from('talent_points')
        .select('balance, total_spent')
        .eq('user_id', user.id)
        .single();

      if (pointError) throw pointError;

      await supabase
        .from('talent_points')
        .update({
          balance: pointData.balance - withdrawAmount,
          total_spent: pointData.total_spent + withdrawAmount,
        })
        .eq('user_id', user.id);

      // 거래 내역 생성
      await supabase
        .from('point_transactions')
        .insert({
          user_id: user.id,
          amount: -withdrawAmount,
          type: 'exchange',
          description: `달란트 환급 신청 (${(withdrawAmount * EXCHANGE_RATE).toLocaleString()}원)`,
        });

      alert('환급 신청이 완료되었습니다.\n영업일 기준 3-5일 내에 처리됩니다.');
      onSuccess();
      onClose();
    } catch (error) {
      console.error('환급 신청 실패:', error);
      alert('환급 신청 중 오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl max-w-md w-full p-6">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-2xl font-bold text-gray-900">달란트 환급</h3>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 cursor-pointer"
          >
            <i className="ri-close-line text-2xl"></i>
          </button>
        </div>

        <div className="bg-teal-50 rounded-lg p-4 mb-6">
          <p className="text-sm text-teal-800 mb-2">
            보유 달란트: <span className="font-bold text-lg">{balance.toLocaleString()}</span>
          </p>
          <p className="text-xs text-teal-600">
            • 최소 환급 금액: {MIN_WITHDRAW.toLocaleString()} 달란트<br />
            • 환급 비율: 1 달란트 = {EXCHANGE_RATE}원<br />
            • 처리 기간: 영업일 기준 3-5일
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              환급 금액
            </label>
            <input
              type="number"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder={`최소 ${MIN_WITHDRAW.toLocaleString()} 달란트`}
              min={MIN_WITHDRAW}
              max={balance}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
              required
            />
            {amount && parseInt(amount) >= MIN_WITHDRAW && (
              <p className="text-sm text-gray-600 mt-1">
                환급 금액: {(parseInt(amount) * EXCHANGE_RATE).toLocaleString()}원
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              은행명
            </label>
            <select
              value={bankName}
              onChange={(e) => setBankName(e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
              required
            >
              <option value="">은행 선택</option>
              <option value="KB국민은행">KB국민은행</option>
              <option value="신한은행">신한은행</option>
              <option value="우리은행">우리은행</option>
              <option value="하나은행">하나은행</option>
              <option value="NH농협은행">NH농협은행</option>
              <option value="IBK기업은행">IBK기업은행</option>
              <option value="SC제일은행">SC제일은행</option>
              <option value="카카오뱅크">카카오뱅크</option>
              <option value="토스뱅크">토스뱅크</option>
              <option value="케이뱅크">케이뱅크</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              계좌번호
            </label>
            <input
              type="text"
              value={accountNumber}
              onChange={(e) => setAccountNumber(e.target.value.replace(/[^0-9]/g, ''))}
              placeholder="'-' 없이 숫자만 입력"
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              예금주
            </label>
            <input
              type="text"
              value={accountHolder}
              onChange={(e) => setAccountHolder(e.target.value)}
              placeholder="예금주명"
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
              required
            />
          </div>

          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <p className="text-xs text-yellow-800">
              <i className="ri-information-line mr-1"></i>
              환급 신청 후 취소가 불가능합니다. 계좌 정보를 정확히 확인해주세요.
            </p>
          </div>

          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-6 py-3 border border-gray-300 text-gray-700 font-semibold rounded-lg hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap"
            >
              취소
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 px-6 py-3 bg-teal-600 text-white font-semibold rounded-lg hover:bg-teal-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap"
            >
              {loading ? '처리 중...' : '신청하기'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
